import type { MouseEvent } from 'react';

import { readConsentPreferences } from '@invideoio/web-shared/utils/cookieConsent';
import { localStorageWithExpiry } from '@utils/localstorage.utils';

const BRANCH_HOST = 'invideo-ai.app.link';

// Arrival keys that carry attribution evidence: utm_* plus these click ids
// and referral markers (the set pro-landing stores, hooks/useOnPageLoad.ts).
const ATTRIBUTION_KEYS = new Set([
  'fbclid',
  'gclid',
  'gbraid',
  'wbraid',
  'msclkid',
  'ttclid',
  'twclid',
  'li_fat_id',
  'irclickid',
  'mpid',
  'campaign_id',
  'adset_id',
  'ad_id',
  'ref',
  'r',
  'via',
  'oppref',
]);

const isAttributionKey = (key: string) =>
  key.startsWith('utm_') || ATTRIBUTION_KEYS.has(key);

/**
 * The stored arrival query. _app.tsx writes it as a raw "?a=b" string;
 * pro-landing writes a key/value record under the same key on this origin.
 */
function storedQueryParams(): Record<string, string> {
  const saved = localStorageWithExpiry.get('qs');
  if (typeof saved === 'string') {
    return Object.fromEntries(new URLSearchParams(saved));
  }
  const params: Record<string, string> = {};
  if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
    Object.entries(saved).forEach(([key, value]) => {
      if (typeof value === 'string') params[key] = value;
    });
  }
  return params;
}

/** True for a Branch app-download link (invideo-ai.app.link). */
export function isBranchAppLink(href: string | null | undefined): boolean {
  if (!href) return false;
  try {
    return new URL(href).hostname === BRANCH_HOST;
  } catch {
    return false;
  }
}

/**
 * Append the landing's stored attribution (utm_*, click ids, referral keys)
 * and domain referrer (`dr`) to a Branch app link. Branch hands the clicked
 * link, query included, to the app as `~referring_link`, and the app
 * classifies that query; a bare link books the install as direct. Anything
 * that isn't a Branch link is returned untouched.
 *
 * CONSENT: this hands ad click ids and UTMs to a third party (Branch), so it
 * sits under the `marketing` category, the same gate as the marketing vendors
 * in LoadAnalytics. Until marketing is granted the link stays bare.
 *
 * A link that already carries its own attribution keeps it as is: stored keys
 * are not spliced in, so a link's utm_source never pairs with a stored
 * utm_medium. Only `dr` is added if missing.
 */
export function decorateAppLink(href: string): string {
  if (!isBranchAppLink(href)) return href;
  if (readConsentPreferences()?.marketing !== true) return href;

  try {
    const url = new URL(href);
    const linkHasAttribution = Array.from(url.searchParams.keys()).some(
      isAttributionKey,
    );
    if (!linkHasAttribution) {
      Object.entries(storedQueryParams()).forEach(([key, value]) => {
        if (isAttributionKey(key)) url.searchParams.set(key, value);
      });
    }

    const domainReferrer = localStorageWithExpiry.get('domain_referrer');
    if (typeof domainReferrer === 'string' && !url.searchParams.has('dr')) {
      url.searchParams.set('dr', domainReferrer);
    }

    return url.toString();
  } catch {
    return href;
  }
}

/**
 * onClick / onAuxClick for an `<a href>` Branch link: decorate it before it
 * navigates (middle-click fires auxclick, not click).
 */
export function decorateAppLinkOnClick(event: MouseEvent<HTMLAnchorElement>) {
  const anchor = event.currentTarget;
  anchor.href = decorateAppLink(anchor.href);
}
